import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import moment from "moment";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { LegacyForm } from "PFApp/components/form/legacy_form";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import PositionPeriodPicker from "PFApp/profiles/edit/fields/position_period_picker";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Select } from "PFComponents/select";
import SidePanel from "PFComponents/side_panel/side_panel";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import api from "PFCore/api";
import {
  customTypesAccessLevelsAgainstProfile,
  ownProfileCustomTypesAccessLevels
} from "PFCore/helpers/custom_types";
import { utc } from "PFCore/helpers/date";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useActivityDelete } from "PFCore/hooks/queries/activity/use_activity_delete";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { createActivity } from "PFCore/services/activities";
import { Activity, Profile } from "PFCore/types";
import { Option } from "PFTypes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./edit_internal_experience_form.module.scss";

const ENGAGEMENT_HOURS_MAX_VALUE = 10 * 365 * 24; // 10 years in hours
const ENGAGEMENT_HOURS_MIN_VALUE = 0;

type EditInternalExperienceFormProps = {
  experience?: Activity;
  adminPage: boolean;
  profile: Profile;
  handleSuccess: (experience: Activity) => void;
  handleRemove: () => void;
  onClose: () => void;
};

const EditInternalExperienceForm = ({
  experience,
  adminPage,
  profile,
  handleSuccess,
  handleRemove,
  onClose
}: EditInternalExperienceFormProps) => {
  const { formatDatabaseDateTime, formatISODate } = useDateFormatter();

  const dateRange = experience?.metadata.date_range || {};

  const [positionId, setPositionId] = useState(experience?.metadata.position_id);
  const [startDate, setStartDate] = useState(
    dateRange.start_date ? formatISODate(utc(dateRange.start_date)) : null
  );
  const [endDate, setEndDate] = useState(dateRange.end_date ? formatISODate(utc(dateRange.end_date)) : null);
  const [current, setCurrent] = useState(dateRange.current || false);
  const [location, setLocation] = useState(experience?.metadata.location || "");
  const [engagementHours, setEngagementHours] = useState(experience?.metadata.engagement_hours);
  const [description, setDescription] = useState(experience?.description || "");
  const [name, setName] = useState(experience?.name || "");
  const [customFields, setCustomFields] = useState(experience?.custom_fields || []);
  const [errors, setErrors] = useState({});

  const { showGrowl } = useGrowlStoreActions();
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("profiles", { keyPrefix: "common.experience" });
  const { t: tBase } = useTranslation();
  const { update: updateActivity } = useActivityUpdate(experience?.id);
  const { mutateAsync: deleteActivity } = useActivityDelete();
  const { getPermittedTypes } = useCustomTypes();
  const workTemplate = useTemplate(TemplateKey.Work);

  const isNew = !experience?.id;
  const engagementHoursMaxValueWarningTip =
    engagementHours >= ENGAGEMENT_HOURS_MAX_VALUE
      ? t("internalExperience.edit.hours.maxValueWarningTip", { value: ENGAGEMENT_HOURS_MAX_VALUE })
      : undefined;

  const handleSuccessCallback = (experience) => {
    showGrowl({
      message: isNew ? t("internalExperience.created") : t("internalExperience.updated")
    });
    handleSuccess &&
      handleSuccess({
        ...experience,
        statistics: {
          ...profile?.statistics
        }
      });
  };

  const handleError = (errors) => {
    setErrors(errors);

    if (errors.base) {
      showGrowl({
        message: errors.base,
        kind: "error"
      });
    }
  };

  const formData = () => ({
    metadata: {
      engagementHours,
      positionId,
      dateRange: {
        startDate: !current && !endDate ? null : formatDatabaseDateTime(startDate),
        endDate: endDate ? formatDatabaseDateTime(endDate) : null,
        current
      },
      location
    },
    customFields: customFields,
    name,
    description
  });

  const isChanged = () => {
    const data = pick(experience, ["custom_fields", "name", "description"]);
    data.metadata = pick(experience?.metadata, ["engagement_hours", "position_id", "date_range", "location"]);
    if (data.metadata.date_range) {
      data.metadata.date_range.current = data.metadata.date_range.current || false;
    }
    return !isEqual(data, formData());
  };

  const handleRemoveCallback = () => {
    if (!isNew) {
      if (window.confirm(tBase("sureToDelete", { key: name }))) {
        deleteActivity({ activityId: experience.id }).then(() => handleRemove && handleRemove());
      }
    } else {
      if (isChanged()) {
        if (window.confirm(t("confirmCloseDialog"))) {
          handleRemove && handleRemove();
        }
      } else {
        handleRemove && handleRemove();
      }
    }
  };

  const handleSubmit = () => {
    setErrors({});
    const payload = {
      templateId: workTemplate?.id,
      postUntil: moment().add(workTemplate?.default_post_until_days, "days").format(),
      ...formData()
    };

    const apiFunction = isNew ? createActivity : updateActivity;
    return apiFunction(payload);
  };

  const getCustomField = (name) => customFields.filter((customField) => customField.type.name === name)[0];

  const getCustomFieldValues = (name) => {
    const customField = getCustomField(name);
    return customField ? customField.values : [];
  };

  const updateCustomField = (customType, values) => {
    const newCustomFields = [...customFields];
    const customField = newCustomFields.find(({ type }) => type.name === customType.name);

    if (customField) {
      customField.values = values;
      setCustomFields(newCustomFields);
    } else {
      setCustomFields((prev) => [
        ...prev,
        {
          type: { id: customType.id, name: customType.name },
          values: values
        }
      ]);
    }
  };

  const permittedCustomTypes = useMemo(() => {
    const ctAccessLevels = adminPage
      ? customTypesAccessLevelsAgainstProfile(profile, currentProfile)
      : ownProfileCustomTypesAccessLevels(currentProfile);

    return getPermittedTypes({ ctAccessLevels });
  }, [adminPage, currentProfile, getPermittedTypes, profile]);

  const getCustomType = (name) => permittedCustomTypes.find((customType) => customType.name === name);

  const brandsCustomType = getCustomType("brands");
  const sectorsCustomType = getCustomType("sectors");
  const skillsCustomType = getCustomType("skills");

  return (
    <SidePanel
      show
      fullHeight
      isOnTop
      size="medium"
      title={isNew ? t("position.newExperience") : t("position.editPosition")}
      onClose={onClose}
    >
      <LegacyForm
        blockNavigationOnChange
        growlErrors
        isDirty={isChanged()}
        isNew={isNew}
        onError={handleError}
        onRemove={handleRemoveCallback}
        onSubmit={handleSubmit}
        onSuccess={handleSuccessCallback}
        qaIdPrefix="profile-edit-past-experience"
      >
        <div className={css.form}>
          <Select
            label={t("internalExperience.edit.position.label")}
            tip={t("internalExperience.edit.position.tip")}
            value={positionId}
            fetchOptions={({ ids }) =>
              api({
                url: "options/positions",
                params: {
                  profileId: profile?.id,
                  value: ids
                }
              }).then((response) => ({
                ...response,
                entries: response.entries.map((position) => ({
                  id: position.id,
                  value: position.text,
                  original: position
                }))
              }))
            }
            error={errors["metadata[positionId]"]}
            onChange={(option: Option<{ id: string; value: string }>) => {
              if (option) {
                setPositionId(option.id);
              }
            }}
            disabled={!!experience?.metadata.position_id}
          />
          <InputFieldSet
            label={t("internalExperience.edit.roleTitle.label")}
            tip={t("internalExperience.edit.roleTitle.tip")}
            value={name}
            inputType="text"
            qaId="name"
            error={errors["name"]}
            onChange={(value) => setName(value)}
          />
          {brandsCustomType && (
            <CustomValuesEditField
              label={brandsCustomType.display_as}
              tip={t("internalExperience.edit.client")}
              customType={brandsCustomType}
              values={getCustomFieldValues("brands")}
              qaIdPrefix="profile-internal-experience"
              errors={errors["customFields[brands]"]}
              adminPage={adminPage}
              multi={false}
              profile={profile}
              handleChange={(values) => updateCustomField(brandsCustomType, values)}
            />
          )}
          {sectorsCustomType && (
            <CustomValuesEditField
              label={sectorsCustomType.display_as}
              tip={t("internalExperience.edit.industries")}
              customType={sectorsCustomType}
              values={getCustomFieldValues("sectors")}
              qaIdPrefix="profile-internal-experience"
              errors={errors["customFields[sectors]"]}
              adminPage={adminPage}
              profile={profile}
              handleChange={(values) => updateCustomField(sectorsCustomType, values)}
            />
          )}
          {skillsCustomType && (
            <CustomValuesEditField
              label={skillsCustomType.display_as}
              tip={t("internalExperience.edit.skills")}
              customType={skillsCustomType}
              values={getCustomFieldValues("skills")}
              qaIdPrefix="profile-internal-experience"
              errors={errors["customFields[skills]"]}
              adminPage={adminPage}
              profile={profile}
              handleChange={(values) => updateCustomField(skillsCustomType, values)}
            />
          )}
          <InputFieldSet
            label={t("internalExperience.edit.location.label")}
            tip={t("internalExperience.edit.location.tip")}
            value={location}
            inputType="text"
            qaId="metadata[location]"
            error={errors["metadata[location]"]}
            onChange={(value) => setLocation(value)}
          />
          <InputFieldSet
            label={t("internalExperience.edit.description.label")}
            tip={t("internalExperience.edit.description.tip")}
            value={description}
            inputType="textarea"
            qaId="description"
            error={errors["description"]}
            onChange={(value) => setDescription(value)}
          />
          <InputFieldSet
            label={t("internalExperience.edit.hours.label")}
            tip={t("internalExperience.edit.hours.tip")}
            value={isNaN(engagementHours) ? undefined : `${engagementHours}`}
            max={ENGAGEMENT_HOURS_MAX_VALUE}
            min={ENGAGEMENT_HOURS_MIN_VALUE}
            inputType="number"
            qaId="metadata[engagementHours]"
            error={errors["metadata[engagementHours]"]}
            warning={engagementHoursMaxValueWarningTip}
            onKeyDown={(event) => {
              if (!event.key.match(/^[0-9]+|Backspace$/)) {
                event.preventDefault();
              }
            }}
            onChange={(value) =>
              setEngagementHours(
                isNaN(parseInt(value)) ? undefined : Math.min(parseInt(value), ENGAGEMENT_HOURS_MAX_VALUE)
              )
            }
          />
          <PositionPeriodPicker
            error={
              errors["metadata[dateRange][startDate]"] ||
              errors["metadata[dateRange][endDate]"] ||
              errors["metadata[dateRange]"]
            }
            showLabel
            currentPosition={current}
            startDate={startDate ? formatISODate(startDate) : null}
            endDate={endDate ? formatISODate(endDate) : null}
            handleToggleClick={(current) => setCurrent(current)}
            handleChange={(startDate, endDate) => {
              setStartDate(startDate);

              if (endDate && !current) {
                setEndDate(endDate);
              } else {
                setEndDate(null);
              }
            }}
          />
        </div>
      </LegacyForm>
    </SidePanel>
  );
};

export default EditInternalExperienceForm;
